import ContinueButton from '@components/continueButton/ContinueButton';
import { useErrorContext } from '@errorHandler/context/ErrorContext';
import { type ApiError, AppError } from '@errorHandler/errors';
import { useSingleFlight } from '@hooks/useSingleFlight';
import { InputBox } from '@imprivata-cloud/components';
import { Form } from 'antd';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

enum FieldNames {
  TOKEN_CODE = 'tokenCode',
}

interface FormValues {
  [FieldNames.TOKEN_CODE]: string;
}

const TRANSLATION = 'self-service.epcs-setup.verify-iid';
const TOKEN_CODE_LENGTH = 6;

interface VerifyAuthenticatorProps {
  authenticatorShortName: string;
  authenticatorDeviceModel?: string;
  /** Submits the token code to whichever assurance family owns the flow. */
  verify: (tokenCode: string) => Promise<void>;
  onVerified: () => void | Promise<void>;
  cancelFlow: () => void;
}

const EpcsVerifyAuthenticator = ({
  authenticatorShortName,
  authenticatorDeviceModel,
  verify,
  onVerified,
  cancelFlow,
}: VerifyAuthenticatorProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm<FormValues>();
  const { emitError } = useErrorContext();
  const tokenCode = Form.useWatch(FieldNames.TOKEN_CODE, form);

  const handleSubmit = useCallback(
    async (values: FormValues) => {
      try {
        await verify(values[FieldNames.TOKEN_CODE]);
        await onVerified();
      } catch (error) {
        emitError(new AppError(error as ApiError));
      }
    },
    [emitError, onVerified, verify],
  );

  // A token code can only be spent once, so a second submit of the same one can only fail.
  const [submit, busy] = useSingleFlight(handleSubmit);

  return (
    <Form<FormValues> className="continue-layout" form={form} noValidate onFinish={submit}>
      <div>
        <h2 className="title">
          {t(`${TRANSLATION}.title`, {
            deviceName: authenticatorDeviceModel || authenticatorShortName,
          })}
        </h2>
        <p>{t(`${TRANSLATION}.instructions`)}</p>
        <Form.Item
          name={FieldNames.TOKEN_CODE}
          rules={[{ required: true, message: t(`${TRANSLATION}.error.token-code-required`) }]}
        >
          <InputBox
            label={t(`${TRANSLATION}.token-code`)}
            type="text"
            aria-label="token-code"
            maxLength={TOKEN_CODE_LENGTH}
            inputMode="numeric"
          />
        </Form.Item>
      </div>

      <div>
        <ContinueButton
          htmlType="submit"
          disabled={tokenCode?.length !== TOKEN_CODE_LENGTH}
          loading={busy}
          secondaryButton={{ label: t('common.do-later'), onClick: cancelFlow }}
        >
          {t('common.continue-button')}
        </ContinueButton>
      </div>
    </Form>
  );
};

export default EpcsVerifyAuthenticator;
